import React, { forwardRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Button,
  Slide,
  useTheme,
  Box,
  CircularProgress,
  Portal,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import { tokens } from "../../theme";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// Transition for the Dialog (slides up)
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Transition for the Snackbar (slides in from the right)
function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

const ExpensesDeleteDialog = ({
  open,
  onClose,
  onConfirm,
  isBulkDelete = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [phase, setPhase] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleConfirmClick = async () => {
    setPhase("loading");
    try {
      // Await the parent's API call
      await onConfirm();

      // Keep loading state visible for 1.5 seconds
      setTimeout(() => {
        setPhase(null);
        onClose();

        // Show the snackbar slightly after the dialog closes and table refreshes
        setTimeout(() => {
          setSnackbarMessage(
            isBulkDelete
              ? "All expenses records deleted successfully!"
              : "Expenses record deleted successfully!",
          );
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }, 300);
      }, 1500);
    } catch (error) {
      setPhase(null);
      setSnackbarMessage("Failed to delete. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (
            reason === "backdropClick" ||
            reason === "escapeKeyDown" ||
            phase === "loading"
          )
            return;
          onClose();
        }}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Transition}
        keepMounted
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            backgroundImage: "none",
            borderRadius: "16px",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 24px 48px rgba(0,0,0,0.5)"
                : "0 24px 48px rgba(0,0,0,0.1)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)"
            }`,
            p: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogTitle sx={{ p: 0, m: 0, textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            <Box
              sx={{
                width: { xs: 56, sm: 64 },
                height: { xs: 56, sm: 64 },
                borderRadius: "50%",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(244, 63, 94, 0.1)"
                    : "rgba(225, 29, 72, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DeleteOutlineIcon
                sx={{
                  fontSize: { xs: 28, sm: 32 },
                  color: colors.redAccent[500],
                }}
              />
            </Box>
          </Box>
          <Typography
            variant="h3"
            fontWeight="800"
            color={colors.gray[100]}
            sx={{ mb: 1, fontSize: { xs: "1.3rem", sm: "1.5rem" } }}
          >
            {isBulkDelete ? "Delete All Records" : "Delete Record"}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: "0 !important", m: 0, mt: 1 }}>
          <Typography
            variant="body1"
            textAlign="center"
            color={colors.gray[300]}
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem" },
              lineHeight: 1.5,
              px: { xs: 0, sm: 4 },
            }}
          >
            {isBulkDelete
              ? "Are you sure you want to delete all expenses records? This action cannot be undone once confirmed."
              : "Are you sure you want to delete this expenses record? This action cannot be undone once confirmed."}
          </Typography>
        </DialogContent>

        <Divider
          sx={{
            mt: { xs: 3, sm: 4 },
            mb: { xs: 2, sm: 3 },
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.12)",
          }}
        />

        <DialogActions
          sx={{
            p: 0,
            m: 0,
            pb: { xs: 2, sm: 1 },
            px: 0,
            justifyContent: "center",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 2, sm: 3 },
            width: "100%",
            "& > :not(style) ~ :not(style)": {
              ml: "0 !important",
            },
          }}
        >
          <Button
            onClick={onClose}
            disabled={phase === "loading"}
            variant="outlined"
            sx={{
              width: "100%",
              color: colors.gray[100],
              borderColor: colors.gray[500],
              textTransform: "none",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              fontWeight: "600",
              px: { xs: 2, sm: 4 },
              py: { xs: 1.2, sm: 1.4 },
              borderRadius: "8px",
              minWidth: { xs: "120px", sm: "140px" },
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                borderColor: colors.gray[300],
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.04)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
              "&.Mui-disabled": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(0,0,0,0.12)",
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.3)"
                    : "rgba(0,0,0,0.3)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClick}
            disabled={phase === "loading"}
            variant="contained"
            sx={{
              width: "100%",
              backgroundColor: colors.redAccent[500],
              color: "#ffffff",
              textTransform: "none",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              fontWeight: "600",
              px: { xs: 2, sm: 4 },
              py: { xs: 1.2, sm: 1.4 },
              borderRadius: "8px",
              minWidth: { xs: "120px", sm: "140px" },
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 4px 10px rgba(0,0,0,0.3)"
                  : "0 4px 12px rgba(225, 29, 72, 0.2)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: colors.redAccent[600],
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 6px 14px rgba(0,0,0,0.4)"
                    : "0 6px 16px rgba(225, 29, 72, 0.3)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "scale(0.95)",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(0,0,0,0.12)",
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.3)"
                    : "rgba(0,0,0,0.3)",
                boxShadow: "none",
              },
            }}
          >
            {phase === "loading" ? (
              <>
                Deleting&nbsp;
                <CircularProgress
                  size={18}
                  sx={{ color: "inherit", ml: "8px" }}
                />
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Portal>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          TransitionComponent={SlideTransition}
          sx={{ mt: 8, zIndex: 9999 }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: "100%",
              backgroundColor:
                snackbarSeverity === "success"
                  ? colors.greenAccent[700]
                  : colors.redAccent[700],
              color: "#ffffff",
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 4px 12px rgba(0, 0, 0, 0.4)"
                  : "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

export default ExpensesDeleteDialog;
